import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Meteor } from 'meteor/meteor';
import { strings } from '/imports/strings';
import { stateOptions } from '/imports/options';
import { getGeoInfo } from '/imports/utils';
import { Org, Orgs, updateMyOrg } from '/imports/api/orgs';
import { Publications } from '/imports/publications';
import { FormField } from '/imports/ui/FormField';
import { Select } from '/imports/ui/Select';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFindOne, useSubscribed, useUser } from '/imports/hooks';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { AddressInput } from '/imports/ui/AddressInput';
import { autoForm, formConfig } from '/imports/ui/AutoForm';

const MyOrgPageStyled = styled(Page)`
  .strict-min {
    margin-top: auto;
  }
  .field.checkbox {
    margin-right: 1.5rem;
  }
`;

let origin: google.maps.LatLngLiteral;
const isDev = ['local', 'development'].includes(Meteor.settings.public.env);

/**
 * Page for viewing and editing the current user's organization details.
 */
export const MyOrgPage = () => {
  const user = useUser();
  const orgId = user?.orgId;
  const orgsLoading = useSubscribed(Publications.listMyOrgs);
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  useEffect(() => {
    (async () => {
      const geoip = isDev ? { origin: { lat: 34.064419, lng: -118.328443 } } : await getGeoInfo();
      origin = geoip.origin;
    })();
  }, []);
  const form = formConfig<Org>([
    [<h2 key="contact">{strings.ContactInformation()}</h2>],
    [
      { field: 'name', label: strings.Name() },
      { field: 'email' },
      { field: 'phone', input: { type: PhoneInput } }
    ],
    [
      {
        field: 'address',
        input: (setUpdate, update, document, error) => {
          const value = update.address || document?.address;
          return (
            <FormField key="address" field="address" error={error}>
              <label>{strings.Address()}</label>
              <AddressInput
                value={value}
                onChange={(address) => setUpdate({ ...update, address })}
                autoCompleteRequest={origin ? { locationBias: { lat: origin.lat, lng: origin.lng } } : undefined}
              />
            </FormField>
          );
        }
      },
      { field: 'suite', label: strings.Suite(), optional: true },
    ],
    [
      { field: 'city', label: strings.City() },
      { field: 'state', label: strings.State(), input: { type: Select, props: { options: [{ value: '', label: '' }, ...stateOptions] } } },
      { field: 'zipcode', label: strings.ZipCode() },
    ],
  ]);
  const [OrgForm] = autoForm({
    document: org,
    createMethod: updateMyOrg,
    updateMethod: updateMyOrg,
    form,
  });
  const initialized = user && !orgsLoading;
  if (!initialized) return <LoadingFlex />;
  return (
    <MyOrgPageStyled className={`page${!initialized ? ' initializing' : ''}`}
      breadcrumbs={[{ path: '/profile', label: strings.Profile() }]}
    >
      <PageHeading
        left={<h1>{strings.Organization()}</h1>}
      />
      <PageBody>
        {OrgForm}
      </PageBody>
    </MyOrgPageStyled>
  );
};